import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Globe, Image, Tag, Plus, Upload } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MerchantApi } from "@/api";
import { BUSINESS_TYPE } from "@/constants/merchant.constant";

interface OfferData {
  id: string;
  name: string;
  description: string;
  discountType: string;
  discountValue: string;
  status: string;
  startDate?: string;
  endDate?: string;
  bannerImage?: string;
  offerUrl?: string;
  category?: string;
  terms?: string;
  // Offline merchant fields
  eligiblePurchase?: string;
  colorCode?: string;
}

interface AddOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddOffer: (offer: OfferData) => void;
  categories: { id: string; name: string }[];
  categoriesLoading?: boolean;
  merchantId: string;
  merchant?: any; // Add merchant data to access businessType
}

// Offer form schema — all visible fields mandatory
const createOfferFormSchema = (isOnlineBusiness: boolean) =>
  z
    .object({
      name: z.string().trim().min(1, { message: "Title is required" }),
      description: z
        .string()
        .trim()
        .min(1, { message: "Description is required" }),
      startDate: z.string().min(1, { message: "Start date is required" }),
      endDate: z.string().min(1, { message: "End date is required" }),
      bannerImage: z.string().min(1, { message: "Image is required" }),
      offerUrl: z
        .string()
        .trim()
        .min(1, { message: "Offer URL is required" })
        .url({ message: "Please enter a valid URL" }),
      category: z.string().min(1, { message: "Category is required" }),
      terms: z
        .string()
        .trim()
        .min(1, { message: "Terms & Conditions are required" }),
      offerEligibility: isOnlineBusiness
        ? z.string().optional()
        : z
            .string()
            .trim()
            .min(1, { message: "Offer Eligibility is required" }),
      colorCode: isOnlineBusiness
        ? z.string().optional()
        : z.string().min(1, { message: "Color Code is required" }),
    })
    .superRefine((data, ctx) => {
      if (data.startDate && data.endDate) {
        const start = new Date(data.startDate);
        const end = new Date(data.endDate);

        if (start > end) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["startDate"],
            message: "Start date cannot be greater than end date",
          });
        }

        if (end < start) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message: "End date must be equal to or greater than start date",
          });
        }
      }
    });

export function AddOfferDialog({
  open,
  onOpenChange,
  onAddOffer,
  categories,
  categoriesLoading = false,
  merchantId,
  merchant,
}: AddOfferDialogProps) {
  const isOnlineBusiness = merchant?.businessType === BUSINESS_TYPE.ONLINE;
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [selectedColor, setSelectedColor] = React.useState("#000000");
  const today = new Date().toISOString().split("T")[0];
  const offerFormSchema = React.useMemo(
    () => createOfferFormSchema(isOnlineBusiness),
    [isOnlineBusiness]
  );
  const form = useForm({
    resolver: zodResolver(offerFormSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      bannerImage: "",
      offerUrl: "",
      category: "",
      terms: "",
      // Offline merchant fields
      offerEligibility: "",
      colorCode: "#000000",
    },
  });

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setImagePreview(preview);
        form.setValue("bannerImage", preview, { shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (data: z.infer<ReturnType<typeof createOfferFormSchema>>) => {
    try {
      const formData = new FormData();
      if (imageFile) {
        formData.append("offerImage", imageFile);
      }
      formData.append("merchantId", merchantId);
      formData.append("title", data.name);
      formData.append("offerURL", data.offerUrl || "");
      formData.append("description", data.description);
      if (data.startDate)
        formData.append(
          "startDateInMillis",
          String(new Date(data.startDate).getTime())
        );
      if (data.endDate)
        formData.append(
          "endDateInMillis",
          String(new Date(data.endDate).getTime())
        );
      // Find categoryId by name
      const cat = categories.find((c) => c.name === data.category);
      if (cat) formData.append("categoryId", cat.id);
      if (data.terms) formData.append("termsAndConditions", data.terms);

      // Add offline merchant fields
      if (!isOnlineBusiness) {
        if (data.offerEligibility)
          formData.append("eligiblePurchase", data.offerEligibility);
        if (data.colorCode) formData.append("colorCode", data.colorCode);
      }

      // Call API
      const response = await MerchantApi.createOrUpdateMerchantOffer(formData);
      toast.success("Offer added successfully");
      form.reset();
      setImageFile(null);
      setImagePreview(null);
      onOpenChange(false);
      // Optionally, trigger parent refresh
      onAddOffer({
        id: response?.data?.id || `offer${Date.now()}`,
        status: "Active",
        name: data.name || "",
        description: data.description || "",
        discountType: "Percentage", // Default value since we removed the field
        discountValue: "0", // Default value since we removed the field
        startDate: data.startDate || "",
        endDate: data.endDate || "",
        bannerImage: response?.data?.imageUri || imagePreview || "",
        offerUrl: data.offerUrl || "",
        category: data.category || "",
        terms: data.terms || "",
        // Include offline merchant fields
        eligiblePurchase: data.offerEligibility || "",
        colorCode: data.colorCode || "",
      });
    } catch (error) {
      toast.error(error?.message || "Failed to add offer");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Add New Offer
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* Image Upload */}
            <FormField
              control={form.control}
              name="bannerImage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Image*</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="offer-image-upload"
                      />
                      <label
                        htmlFor="offer-image-upload"
                        className="cursor-pointer block"
                      >
                        <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-gray-400 transition-colors">
                          {imagePreview ? (
                            <div className="relative w-full h-full">
                              <img
                                src={imagePreview}
                                alt="Offer preview"
                                className="w-full h-full object-cover rounded-lg"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                                <Upload className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          ) : (
                            <>
                              <Plus className="h-12 w-12 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">
                                Click to upload offer image
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                PNG, JPG up to 10MB
                              </p>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title*</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Summer Discount" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description*</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Offer description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="offerUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Offer URL*</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://example.com/offer"
                        {...field}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard
                            .readText()
                            .then((text) => {
                              if (text.startsWith("http")) {
                                field.onChange(text);
                                toast.info("URL pasted");
                              }
                            })
                            .catch(() => {
                              toast.error("Could not read from clipboard");
                            });
                        }}
                      >
                        <Globe className="h-4 w-4" />
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date*</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date*</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={form.watch("startDate") || today}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category*</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            categoriesLoading
                              ? "Loading..."
                              : "Select a category"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categoriesLoading ? (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          Loading...
                        </div>
                      ) : categories.length === 0 ? (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          No categories found
                        </div>
                      ) : (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.name}>
                            {category.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terms & Conditions*</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter offer terms and conditions"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isOnlineBusiness && (
              <>
                <FormField
                  control={form.control}
                  name="offerEligibility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Offer Eligibility*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter offer eligibility criteria"
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="colorCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Color Code*</FormLabel>
                      <FormControl>
                        <div className="flex gap-2 items-center">
                          <div className="relative">
                            <div
                              className="w-10 h-10 rounded border cursor-pointer hover:border-gray-400 transition-colors"
                              style={{ backgroundColor: selectedColor }}
                            />
                            <input
                              type="color"
                              value={selectedColor}
                              onChange={(e) => {
                                setSelectedColor(e.target.value);
                                field.onChange(e.target.value);
                              }}
                              className="absolute inset-0 opacity-0 cursor-pointer w-10 h-10"
                            />
                          </div>
                          <Input
                            placeholder="#000000"
                            value={selectedColor}
                            onChange={(e) => {
                              setSelectedColor(e.target.value);
                              field.onChange(e.target.value);
                            }}
                            className="flex-1"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <DialogFooter className="sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Add Offer</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
